import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
} from "@mui/material";
import { apiVariable } from "../../api/apiVariable";
import { getPublicRequestError } from "../../helpers/publicRequestError";

const COOKIE_CONSENT_KEY = "candidate-cookie-consent";

const ExternalCandidateFormPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState(() => ({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    position: searchParams.get("position") ?? "",
    email: "",
  }));
  const [cv, setCv] = useState<File | null>(null);
  const [errors, setErrors] = useState<any>({});
  const [submissionStatus, setSubmissionStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    try {
      return window.localStorage.getItem(COOKIE_CONSENT_KEY) !== "accepted";
    } catch {
      return true;
    }
  });

  const acceptCookies = () => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    } catch {
      // The choice still applies for this visit when storage is unavailable.
    }
    setShowCookieBanner(false);
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName || formData.firstName.length > 100) {
      newErrors.firstName =
        "First name is required and cannot exceed 100 characters.";
    }
    if (!formData.lastName || formData.lastName.length > 100) {
      newErrors.lastName =
        "Last name is required and cannot exceed 100 characters.";
    }
    const phoneRegex = /^\d{9}$/;
    if (!formData.phoneNumber || !phoneRegex.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Valid phone number is required (9 digits).";
    }
    if (!formData.position || formData.position.length > 100) {
      newErrors.position =
        "Position is required and cannot exceed 100 characters.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (
      !formData.email ||
      !emailRegex.test(formData.email) ||
      formData.email.length > 254
    ) {
      newErrors.email =
        "Valid email is required and cannot exceed 254 characters.";
    }
    if (!cv) {
      newErrors.cv = "CV is required.";
    } else {
      const ext = cv.name.split(".").pop()?.toLowerCase();
      if (ext !== "pdf") {
        newErrors.cv = "Only PDF files are allowed.";
      } else if (cv.size > 5 * 1024 * 1024) {
        newErrors.cv = "File size exceeds limit (5MB).";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    setCv(file);
    setErrors({ ...errors, cv: "" });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    setLoading(true);
    setSubmissionStatus("");
    try {
      const form = new FormData();
      form.append("FirstName", formData.firstName);
      form.append("LastName", formData.lastName);
      form.append("PhoneNumber", formData.phoneNumber);
      form.append("Position", formData.position);
      form.append("Email", formData.email);
      if (cv) {
        form.append("Cv", cv);
      }
      const response = await fetch(`${apiVariable}/external-candidate`, {
        method: "POST",
        body: form,
      });
      if (response.ok) {
        setSubmissionStatus("Candidate submitted successfully.");
        setFormData({
          firstName: "",
          lastName: "",
          phoneNumber: "",
          position: "",
          email: "",
        });
        setCv(null);
      } else if (response.status === 400) {
        const data = await response.json();
        setSubmissionStatus(
          data?.errors
            ? Object.values(data.errors).join(" ")
            : "Invalid input data.",
        );
      } else {
        setSubmissionStatus(
          getPublicRequestError(
            response.status,
            response.headers.get("Retry-After"),
          ),
        );
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmissionStatus("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          padding: 3,
          boxShadow: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Submit Your Application
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Fields with <span style={{ color: "red" }}>*</span> are mandatory
        </Typography>
        {submissionStatus && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {submissionStatus}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                error={Boolean(errors.firstName)}
                helperText={errors.firstName}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                error={Boolean(errors.lastName)}
                helperText={errors.lastName}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="phoneNumber"
                name="phoneNumber"
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={handleChange}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="position"
                name="position"
                label="Position"
                value={formData.position}
                onChange={handleChange}
                error={Boolean(errors.position)}
                helperText={errors.position}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={Boolean(errors.email)}
                helperText={errors.email}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" component="label" fullWidth>
                Upload CV *
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              {errors.cv && (
                <Typography variant="caption" color="error">
                  {errors.cv}
                </Typography>
              )}
              {cv && (
                <Typography variant="caption" display="block">
                  {cv.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Before submitting, please read our{" "}
                <Link
                  component="button"
                  type="button"
                  onClick={() => setPrivacyPolicyOpen(true)}
                  sx={{ verticalAlign: "baseline" }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
      <Dialog
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
        aria-labelledby="candidate-privacy-policy-title"
        scroll="paper"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="candidate-privacy-policy-title">
          Candidate Privacy Policy
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" component="h2" gutterBottom>
            Information we collect
          </Typography>
          <Typography paragraph>
            When you apply, we collect the details entered in this form,
            including your name, phone number, email address, the position you
            are applying for, and your uploaded CV and any information it
            contains.
          </Typography>
          <Typography variant="h6" component="h2" gutterBottom>
            How we use your information
          </Typography>
          <Typography paragraph>
            We use submitted candidate data and uploaded documents only for
            recruitment purposes. This includes reviewing your qualifications,
            contacting you about your application, arranging interviews, and
            making hiring decisions. Information is shared only with staff and
            service providers who need it to support the recruitment process.
          </Typography>
          <Typography variant="h6" component="h2" gutterBottom>
            Storage and your choices
          </Typography>
          <Typography paragraph>
            We keep candidate information only for as long as needed for the
            recruitment process and applicable legal or record-keeping
            requirements, and protect it using appropriate safeguards. You may
            contact the recruiting team to ask about, correct, or request
            deletion of your candidate information, subject to applicable law.
          </Typography>
          <Typography variant="h6" component="h2" gutterBottom>
            Cookies and local storage
          </Typography>
          <Typography>
            This page does not place non-essential cookies before consent. If
            you select “Accept,” we save that choice in your browser's local
            storage so this notice is not shown again on this browser.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrivacyPolicyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {showCookieBanner && (
        <Paper
          component="aside"
          role="region"
          aria-label="Cookie consent"
          elevation={8}
          sx={{
            position: "fixed",
            left: { xs: 12, sm: 24 },
            right: { xs: 12, sm: 24 },
            bottom: { xs: 12, sm: 24 },
            zIndex: (theme) => theme.zIndex.snackbar,
            p: 2,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="body2">
            We use only essential browser storage to remember your cookie
            choice. No non-essential cookies are used before you consent.{" "}
            <Link
              component="button"
              type="button"
              onClick={() => setPrivacyPolicyOpen(true)}
            >
              Read the Privacy Policy
            </Link>
          </Typography>
          <Button
            variant="contained"
            onClick={acceptCookies}
            sx={{ flexShrink: 0 }}
          >
            Accept
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default ExternalCandidateFormPage;
